export interface User {
  id?: string;
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: 'customer' | 'provider' | 'crew' | 'admin';
  phone?: string;
  address?: Address;
  /** Shared sales sandbox provider */
  isDemo?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Customer extends User {
  userType: 'customer';
  address: Address;
  preferences?: CustomerPreferences;
}

export interface Provider extends User {
  userType: 'provider';
  businessName: string;
  serviceAreas: string[];
  services: ServiceType[];
  crewSize: number;
  insuranceProof?: string;
  insuranceExpiryDate?: Date;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceVerified: boolean;
  isVerified: boolean;
  stripeAccountId?: string; // Stripe Connect Express account ID
  stripeAccountOnboardingComplete?: boolean; // Whether the provider has completed Stripe onboarding
  // Pesticide License (IDOA Commercial Pesticide Applicator License - Required for Weed Control/Fertilizing)
  pesticideLicenseOnFile: boolean;
  pesticideLicenseDocumentUrl?: string;
  pesticideLicenseNumber?: string;
  pesticideLicenseExpiryDate?: string;
  pesticideLicenseSubmittedDate?: string;
  rating: number;
  totalJobs: number;
  businessLogo?: string;
  /** Per-state sales tax % (provider remits). Derived from service-area ZIPs. */
  salesTaxRates?: Array<{
    state: string;
    residentialPercent: number;
    commercialPercent: number;
  }>;
  salesTaxCoverageAttested?: boolean;
  salesTaxCoverageAttestedAt?: Date;
}

export interface Crew extends User {
  userType: 'crew';
  providerId: string;
  crewNumber?: number;
  isAvailable: boolean;
  currentLocation?: {
    lat: number;
    lng: number;
    timestamp: Date;
  };
}

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface CustomerPreferences {
  preferredProviders?: string[];
  notificationSettings: {
    email: boolean;
    sms: boolean;
  };
  ecoFriendly: boolean;
}

export interface ServiceType {
  id: string;
  name: string;
  description: string;
  category: 'mowing' | 'fertilizing' | 'snow_removal' | 'other';
  basePrice?: number;
  isActive: boolean;
}

export interface ServiceRequest {
  id: string;
  customerId: string;
  providerId?: string;
  serviceType: ServiceType;
  address: Address;
  description: string;
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract';
  lotSize: number;
  propertyType: 'single_residential' | 'multi_family_condo' | 'commercial' | 'vacant_lot' | 'hoa_managed';
  isImportedCustomer: boolean;
  specialInstructions?: string;
  status: 'pending' | 'bidding' | 'accepted' | 'scheduled' | 'in-progress' | 'completed' | 'cancelled' | 'pending_cancellation' | 'expired_unaccepted';
  requestedDate: Date;
  scheduledDate?: Date;
  completedDate?: Date;
  scheduledTime?: string; // e.g., "09:00", "14:30"
  estimatedDuration?: number; // in hours
  completionNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Bid {
  id: string;
  _id?: string;
  requestId: string | ServiceRequest;
  providerId: string;
  price: number; // Total bid amount (sum of all services)
  providerCut: number; // 95-99% of service price (99% onboarded, 95% marketplace)
  platformCut: number; // 1-5% of service price
  description: string;
  estimatedDuration: number; // Total estimated duration in hours
  validUntil: Date;
  status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'paid';
  createdAt: Date;
  // New per-service pricing structure
  servicePricing: ServicePricing[];
}

export interface ServicePricing {
  service: string; // 'mowing', 'edging', 'fertilizingWeedControl', 'snowRemoval'
  serviceName: string; // Display name like 'Lawn Mowing', 'Snow Removal'
  price: number; // Price for this specific service
  duration: number; // Duration in hours for this service
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract';
  isSeasonalContract: boolean; // true for seasonal contracts
  seasonalContractDetails?: {
    contractStartDate: Date;
    contractEndDate: Date;
    totalContractPrice: number;
    paymentSchedule: 'single-payment' | 'monthly';
    serviceFrequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
    monthlyPaymentAmount?: number; // Monthly payment amount (for monthly payment contracts)
    stripeSubscriptionId?: string; // Stripe subscription ID (for monthly payment contracts)
    subscriptionStatus?: string; // Subscription status (for monthly payment contracts)
  };
}

export interface JobLog {
  id: string;
  jobId: string;
  providerId: string;
  startTime: Date;
  endTime?: Date;
  crewSize: number;
  notes?: string;
  issues?: string[];
  completed: boolean;
  createdAt: Date;
}

export interface Payment {
  id: string;
  _id?: string; // MongoDB _id field (fallback)
  jobId: string;
  serviceRequestId?: any; // ServiceRequest reference
  customerId: any; // User reference
  providerId: string;
  bidId?: any; // Bid reference
  amount: number;
  commission: number;
  aiFees: number;
  netAmount: number;
  providerCut?: number;
  platformCut?: number;
  stripePaymentId?: string;
  stripePaymentIntentId?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'processing';
  createdAt: Date;
  completedAt?: Date;
  // Service information - stores the actual service performed
  serviceType?: {
    id: string;
    name: string;
    category: 'mowing' | 'fertilizing' | 'snow_removal' | 'other';
  };
  onboardedServices?: string[]; // For onboarded customers without bids
}

export interface Review {
  id: string;
  serviceRequestId: string;
  customerId: string;
  providerId: string;
  rating: number; // 1-5 stars
  title?: string;
  comment: string;
  isPublic: boolean;
  createdAt: Date;
}

export interface Analytics {
  totalJobs: number;
  completedJobs: number;
  totalRevenue: number;
  averageRating: number;
  customerRetention: number;
  monthlyGrowth: number;
}

export interface Route {
  id: string;
  providerId: string;
  date: Date;
  jobs: string[]; // job IDs
  totalDistance: number;
  estimatedDuration: number;
  weatherConditions?: string;
  optimized: boolean;
}

export interface Notification {
  id: string;
  userId: string;
  type: 'bid_received' | 'bid_accepted' | 'job_scheduled' | 'payment_received' | 'reminder';
  title: string;
  message: string;
  read: boolean;
  createdAt: Date;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/** Default cancellation cutoff text when not provided by server (must match CANCELLATION_CUTOFF_* env on server if changed). */
export const CANCELLATION_CUTOFF_UI_DEFAULT = '3:00 PM, 3 business days prior to the scheduled service';

export interface WebsiteSettings {
  id: string;
  siteName: string;
  siteLogo?: string;
  /** Human-readable cancellation cutoff (e.g. "3:00 PM, 3 business days prior to the scheduled service"). From server env. */
  cancellationCutoffDescription?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  contactEmail: string;
  contactPhone?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  platformFees: {
    standardFeePercentage: number;
    importedCustomerFeePercentage: number;
    renewalFeePercentage?: number;
  };
  metaDescription?: string;
  metaKeywords?: string[];
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CustomerBackupBid {
  id: string;
  customerId: string;
  serviceRequestId: string;
  acceptedBidId: string;
  acceptedBidPrice: number;
  backupBids: {
    bidId: string;
    providerId: string;
    providerName: string;
    providerPhone: string;
    providerEmail: string;
    price: number;
    bidCreatedAt: Date;
    notes?: string;
  }[];
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerProfile {
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address?: Address;
    createdAt: Date;
  };
  currentServices: ServiceRequest[];
  backupBids: CustomerBackupBid[];
  totalSpent: number;
  totalJobs: number;
  previousServiceRequests?: Array<{
    id: string;
    status: string;
    scheduledDate?: Date | string;
    completedDate?: Date | string;
    providerId?: string;
    providerName?: string;
  }>;
  nextServiceRequests?: Array<{
    id: string;
    status: string;
    scheduledDate?: Date | string;
    providerId?: string;
    providerName?: string;
  }>;
}

export interface ProviderMapData {
  providers: {
    id: string;
    location: {
      lat: number;
      lng: number;
      address: string;
      zipCode: string;
    };
    serviceAreas: string[];
    isVerified: boolean;
  }[];
  serviceZipCodes: string[];
}

export interface ExistingCustomer {
  id: string;
  _id?: string; // MongoDB _id field (fallback for when id is not set)
  providerId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address: Address;
  serviceSchedule: {
    frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract';
    dayOfWeek: number;
    preferredTime?: string;
    notes?: string;
    firstServiceDate?: string;
  };
  paymentStructure?: {
    type: 'one-time' | 'recurring' | 'seasonal-contract';
    amount: number;
    frequency?: 'weekly' | 'bi-weekly' | 'monthly';
    nextPaymentDate?: Date;
    contractStartDate?: Date;
    contractEndDate?: Date;
    isPaymentSetup: boolean;
    paymentSetupDate?: Date;
    firstPaymentProcessed: boolean;
    firstPaymentDate?: Date;
  };
  services: {
    lawnMowingTrimming: boolean;
    weedControlFertilizing: boolean;
    snowRemoval: boolean;
  };
  baggingRequired?: boolean;
  /** Selected services still missing a price above $0 (only while awaiting acceptance). */
  unpricedServices?: string[];
  /** Provider may set prices until the customer accepts. */
  canEditPricing?: boolean;
  servicePricing?: {
    lawnMowingTrimming?: {
      price: number;
      frequency: string;
      recurringDayOfWeek?: number; // 0-6 (Sunday-Saturday) for weekly/bi-weekly services
      recurringDayOfMonth?: number; // 1-31 for monthly services
      biWeeklyStartDate?: Date; // Start date for bi-weekly pattern
      isSeasonalContract?: boolean;
      seasonalContractDetails?: {
        serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
        contractStartDate?: Date;
        contractEndDate?: Date;
        totalContractPrice?: number; // Total contract price (for consistency with Bid model)
        paymentSchedule?: 'single-payment' | 'monthly'; // Payment schedule (for consistency with Bid model)
        monthlyPaymentAmount?: number; // Monthly payment amount (for monthly payment contracts)
        stripeSubscriptionId?: string; // Stripe subscription ID (for monthly payment contracts)
        subscriptionStatus?: string; // Subscription status (for monthly payment contracts)
      };
    } | null;
    weedControlFertilizing?: {
      price: number;
      frequency: string;
      recurringDayOfWeek?: number; // 0-6 (Sunday-Saturday) for weekly/bi-weekly services
      recurringDayOfMonth?: number; // 1-31 for monthly services
      biWeeklyStartDate?: Date; // Start date for bi-weekly pattern
      isSeasonalContract?: boolean;
      seasonalContractDetails?: {
        serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
        contractStartDate?: Date;
        contractEndDate?: Date;
        totalContractPrice?: number; // Total contract price (for consistency with Bid model)
        paymentSchedule?: 'single-payment' | 'monthly'; // Payment schedule (for consistency with Bid model)
        monthlyPaymentAmount?: number; // Monthly payment amount (for monthly payment contracts)
        stripeSubscriptionId?: string; // Stripe subscription ID (for monthly payment contracts)
        subscriptionStatus?: string; // Subscription status (for monthly payment contracts)
      };
    } | null;
    snowRemoval?: {
      price: number;
      frequency: string;
      recurringDayOfWeek?: number; // 0-6 (Sunday-Saturday) for weekly/bi-weekly services
      recurringDayOfMonth?: number; // 1-31 for monthly services
      biWeeklyStartDate?: Date; // Start date for bi-weekly pattern
      isSeasonalContract?: boolean;
      seasonalContractDetails?: {
        serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
        contractStartDate?: Date;
        contractEndDate?: Date;
        totalContractPrice?: number; // Total contract price (for consistency with Bid model)
        paymentSchedule?: 'single-payment' | 'monthly'; // Payment schedule (for consistency with Bid model)
        monthlyPaymentAmount?: number; // Monthly payment amount (for monthly payment contracts)
        stripeSubscriptionId?: string; // Stripe subscription ID (for monthly payment contracts)
        subscriptionStatus?: string; // Subscription status (for monthly payment contracts)
      };
    } | null;
  };
  serviceType?: string; // Keep for backward compatibility
  price: number;
  isActive: boolean;
  importedAt: Date;
  isFirstSeason: boolean;
  customerSource?: 'bid' | 'onboarded'; // Track how customer was added
  linkedCustomerId?: string; // Added for tracking onboarded customers
  linkedAt?: Date;
  paymentStatus?: 'not_setup' | 'account_created_no_payment' | 'setup'; // Payment status
  paymentMethodDetails?: {
    hasActivePaymentMethod: boolean;
    cardCount: number;
    defaultCard?: {
      cardLast4?: string;
      cardBrand?: string;
      cardExpMonth?: number;
      cardExpYear?: number;
    };
    lastUsedAt?: Date;
    // Card verification fields
    verificationStatus?: 'verified' | 'pending' | 'failed';
    verificationDate?: string;
    verificationAmount?: string;
    cardLast4?: string;
    cardBrand?: string;
  };
  invitationId?: string; // Invitation ID if pending
  invitationStatus?: string; // Invitation status
  invitationSentAt?: Date; // When invitation was sent
  lastServiceDate?: Date; // Last completed service date
  nextServiceDate?: Date; // Next scheduled service date
  isOnHold?: boolean; // Payment hold status
  onHoldReason?: string; // Reason for payment hold
  onHoldSince?: Date; // When payment hold was applied
  isDeleted?: boolean; // True when customer deletes their account
  deletedAt?: Date; // Timestamp when customer account was deleted
  createdAt: Date;
  updatedAt: Date;
}

export interface RouteOptimizationSettings {
  enabled: boolean;
  defaultCrewSize: number;
  isFirstSeason: boolean;
  firstSeasonEndDate?: Date;
}

export interface RouteStop {
  type: 'platform' | 'existing';
  customerId?: string;
  existingCustomerId?: string;
  address: Address;
  serviceType: string;
  estimatedDuration: number;
  preferredTime?: string;
  notes?: string;
  order: number;
}

export interface OptimizedRoute {
  crewNumber: number;
  stops: RouteStop[];
  totalDistance: number;
  totalDuration: number;
  startLocation: {
    lat: number;
    lng: number;
  };
  endLocation: {
    lat: number;
    lng: number;
  };
}

export interface RouteOptimization {
  id: string;
  providerId: string;
  date: Date;
  crewSize: number;
  routes: OptimizedRoute[];
  optimizationStatus: 'pending' | 'completed' | 'failed';
  createdAt: Date;
  updatedAt: Date;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  userType: 'customer' | 'provider';
  // Customer-specific fields
  address?: Address;
  // Provider-specific fields
  businessName?: string;
  serviceAreas?: string[];
  salesTaxRates?: Array<{
    state: string;
    residentialPercent: number;
    commercialPercent: number;
  }>;
  services?: string[];
  crewSize?: number;
  insuranceProof?: string;
  insuranceExpiryDate?: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  // Agreement acceptance
  agreedToTerms: boolean;
  agreedToStripeTerms: boolean;
}

export interface JobCompletion {
  id: string;
  serviceRequestId: string;
  crewId: string;
  providerId: string;
  customerId: string;
  checkInTime: Date;
  checkInLocation: {
    lat: number;
    lng: number;
  };
  checkOutTime?: Date;
  checkOutLocation?: {
    lat: number;
    lng: number;
  };
  actualDuration?: number;
  crewSize: number;
  notes?: string;
  issues?: string[];
  beforePhotos?: string[];
  afterPhotos?: string[];
  status: 'checked_in' | 'in_progress' | 'completed' | 'cancelled';
  completedAt?: Date;
  paymentProcessed: boolean;
  paymentAmount?: number;
  stripePaymentId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SeasonalRenewal {
  id: string;
  customerId: string;
  currentProviderId: string;
  season: number;
  lastYearServiceType: string;
  lastYearPrice: number;
  lastYearFrequency: string;
  currentProviderOffer: {
    price: number;
    offerDate: Date;
    description?: string;
  };
  customerDecision?: 'accepted_current' | 'requested_fresh_bids' | 'dismissed';
  customerDecisionDate?: Date;
  bannerDismissed: boolean;
  freshBidsRequested: boolean;
  freshBidsRequestDate?: Date;
  alternativeProviderIds: string[];
  alternativeBids: {
    providerId: string;
    price: number;
    description: string;
    submittedAt: Date;
  }[];
  lowestAlternativeBid?: {
    providerId: string;
    price: number;
  };
  providerMatchDecision?: 'matched' | 'declined';
  providerMatchDecisionDate?: Date;
  matchedPrice?: number;
  status: 'pending' | 'accepted' | 'matched' | 'declined' | 'expired';
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
}
